import { isDefined, isUndefined, schAloftArea } from '@aloft/toolkit-shared';
import { colors } from '@aloft/toolkit-sui';

import {
  CartesianGrid,
  ComposedChart,
  DefaultLegendContent,
  DefaultLegendContentProps,
  Legend,
  Line,
  ReferenceArea,
  Scatter,
} from 'recharts';

import { selectEditablePropertyDataById } from '../../../redux/features/selectors/propertyDataSelectors/editablePropertyDataSelectors.ts';
import { selectSiteCostPerSf } from '../../../redux/features/selectors/selectSiteCostPerSf.ts';
import { useAppSelector } from '../../../redux/hooks.ts';
import { GraphTitle } from '../components/GraphTitle.tsx';
import { GraphXAxis } from '../components/GraphXAxis.tsx';
import { GraphYAxis } from '../components/GraphYAxis.tsx';
import { ResponsiveGraphWrapper } from '../components/ResponsiveGraphWrapper.tsx';
import { SimpleGraphTooltip } from '../components/SimpleGraphTooltip.tsx';
import {
  ExponentialFitDataPoint,
  SubjectVacantLandSalesChartData,
  VacantLandSalesChartData,
  VacantLandSalesGraphProps,
} from './types.ts';

type VacantLandCostPerSFVsLotSizeGraphProps = VacantLandSalesGraphProps & {
  exponentialFitDataPoints: ExponentialFitDataPoint[];
};

const LegendContent = ({ payload, ...props }: DefaultLegendContentProps) => (
  <DefaultLegendContent
    {...props}
    payload={[
      ...payload!,
      {
        value: '25th-75th Percentile',
        type: 'rect',
        color: colors.graphAreaBackground,
      },
    ]}
  />
);

export const VacantLandCostPerSFVsLotSizeGraph = ({
  disableAnimations = false,
  width,
  height,
  vacantLandSalesChartData,
  exponentialFitDataPoints,
}: VacantLandCostPerSFVsLotSizeGraphProps) => {
  const subjectEditableData = useAppSelector((state) =>
    selectEditablePropertyDataById(state, 'subject'),
  );

  const subjectPropertyIdentification = subjectEditableData?.propertyIdentification;
  const subjectSiteArea = subjectEditableData?.site?.area;

  const subjectCostPerSF = useAppSelector((state) =>
    selectSiteCostPerSf(state, 'subject'),
  )?.toDollarLike();

  const subjectDataPoint: SubjectVacantLandSalesChartData | undefined =
    subjectSiteArea && isDefined(subjectCostPerSF)
      ? {
          address: subjectPropertyIdentification?.address?.AddressLineText,
          saleDate: subjectEditableData?.transaction.saleDate,
          siteSize: schAloftArea.parse(subjectSiteArea).getUnitAmount('sqft'),
          subjectCostPerSF,
        }
      : undefined;

  if (!subjectDataPoint) return null;

  const data: (
    | SubjectVacantLandSalesChartData
    | VacantLandSalesChartData
    | ExponentialFitDataPoint
  )[] = [subjectDataPoint, ...vacantLandSalesChartData, ...exponentialFitDataPoints];

  const minCostPerSF = Math.min(...vacantLandSalesChartData.map((d) => d.costPerSF));
  const maxCostPerSF = Math.max(...vacantLandSalesChartData.map((d) => d.costPerSF));

  const p25CostPerSF = minCostPerSF + (maxCostPerSF - minCostPerSF) * 0.25;
  const p75CostPerSF = minCostPerSF + (maxCostPerSF - minCostPerSF) * 0.75;

  return (
    <ResponsiveGraphWrapper disableAnimations={disableAnimations} width={width} height={height}>
      <ComposedChart
        data={data}
        width={width}
        height={height}
        margin={{ top: 32, left: 8, bottom: 8, right: 8 }}
        style={{ background: colors.graphBackground, borderRadius: 8 }}
      >
        <GraphTitle>Vacant Land $ / SF vs Lot Size</GraphTitle>
        <CartesianGrid strokeDasharray="3 3" />
        <Legend verticalAlign="top" align="right" content={LegendContent} />

        <SimpleGraphTooltip<VacantLandSalesChartData & SubjectVacantLandSalesChartData>
          disableAnimations={disableAnimations}
          titleKey="address"
          rows={[
            {
              label: 'Site Size',
              key: 'siteSize',
              color: 'graphChart',
              valueFormatter: (v) => `${v} SF`,
            },
            {
              label: 'Cost Per SF',
              key: 'subjectCostPerSF',
              color: 'graphAdverse',
              valueFormatter: (v) => `$${v}`,
              shouldHide: (p) => isUndefined(p.subjectCostPerSF),
            },
            {
              label: 'Cost Per SF',
              key: 'costPerSF',
              color: 'graphChart',
              valueFormatter: (v) => `$${v}`,
              shouldHide: (p) => isDefined(p.subjectCostPerSF),
            },
            {
              label: 'Sale Date',
              key: 'saleDate',
            },
            {
              label: 'Distance To Subject',
              key: 'distanceToSubject',
              valueFormatter: (v) => (v ? `${v} miles` : '-'),
              shouldHide: (p) => isDefined(p.subjectCostPerSF),
            },
          ]}
        />

        <GraphXAxis
          type="number"
          dataKey="siteSize"
          label="Lot Size (SF)"
          tickFormatter={(val: number) => val.toLocaleString()}
        />
        <GraphYAxis
          type="number"
          label="$ / SF"
          tickFormatter={(val: number) => `$${val.toLocaleString()}`}
        />

        <Scatter
          name="Vacant Land Sales"
          dataKey="costPerSF"
          fill={colors.graphChart}
          isAnimationActive={!disableAnimations}
        />
        <Scatter
          name="Subject Property"
          dataKey="subjectCostPerSF"
          shape="star"
          legendType="star"
          fill={colors.graphAdverse}
          isAnimationActive={!disableAnimations}
        />

        <Line
          name="Exponential Fit"
          dataKey="lineY"
          type="monotone"
          stroke={colors.graphChart4}
          dot={false}
          activeDot={false}
          isAnimationActive={!disableAnimations}
        />
        <ReferenceArea y1={p25CostPerSF} y2={p75CostPerSF} fill={colors.graphAreaBackground} />
      </ComposedChart>
    </ResponsiveGraphWrapper>
  );
};
